import React, {FC, useState} from "react";
import {Tag, Tooltip} from "antd";
import classNames from "classnames";
import moment from "moment";
import {getMollieStatusTagDescriptor} from "./helpers/mollieStatusTagColor";
import {mollieMethodLabel} from "../../../../helpers/mollieMethodLabels";
import {renderAmount} from "../../../../helpers/renderAmount";

const css = require("./MolliePaymentSection.less");

// Phase 23 / Plan 23-05 / BDSEC-05 — History-Expander (D-08 default zugeklappt).
//
// Sketch Variant D (`mollie-status-and-actions.md` Z.102-120, 152-170):
//   <div class="history-expander" onclick="toggleHistory()">
//     ▶ Frühere Zahlungsversuche anzeigen [N]
//   </div>
//   <div class="history-content">
//     <div class="attempt-compact">
//       <tag/> <info> <id/> <amount/>
//     </div>
//   </div>

export interface CompactAttemptShape {
    id: string;
    molliePaymentId: string;
    status: string;
    mollieMethod?: string | null;
    amount?: number | null;
    createDate?: string | Date | null;
    errorMessage?: string | null;
}

export type HistoryExpanderProps = {
    attempts: ReadonlyArray<CompactAttemptShape>;
    onAttemptClick?: (attemptId: string) => void;
};

interface CompactRowProps {
    attempt: CompactAttemptShape;
    onClick?: () => void;
}

const CompactRow: FC<CompactRowProps> = ({attempt, onClick}) => {
    const tagDescriptor = getMollieStatusTagDescriptor(attempt.status);
    const methodLabel = attempt.mollieMethod ? mollieMethodLabel(attempt.mollieMethod) : null;
    const dateString = attempt.createDate ? moment(attempt.createDate).format("L LT") : "—";
    const titleText = methodLabel
        ? `${methodLabel} — ${tagDescriptor.label}`
        : tagDescriptor.label;

    return (
        <div
            className={css.attemptCompact}
            data-testid={`history-attempt-${attempt.id}`}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(e) => {
                if (onClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            <Tag color={tagDescriptor.color} style={{margin: 0}}>
                {tagDescriptor.label}
            </Tag>
            <div className={css.compactInfo}>
                <div className={css.compactTitle}>{titleText}</div>
                <div className={css.compactMeta}>
                    {dateString}
                    {attempt.errorMessage && ` · ${attempt.errorMessage}`}
                </div>
            </div>
            <Tooltip title={attempt.molliePaymentId}>
                <span className={css.compactId}>{attempt.molliePaymentId}</span>
            </Tooltip>
            <span className={css.compactAmount}>
                {attempt.amount != null ? renderAmount({amount: attempt.amount}) : "—"}
            </span>
        </div>
    );
};

export const HistoryExpander: FC<HistoryExpanderProps> = ({attempts, onAttemptClick}) => {
    const [expanded, setExpanded] = useState<boolean>(false); // D-08 default zugeklappt

    if (!attempts || attempts.length === 0) {
        // Keine Past-Attempts → kein Expander.
        return null;
    }

    return (
        <div data-testid="history-expander-wrapper">
            <div
                className={classNames(css.historyExpander, expanded && css.open)}
                role="button"
                tabIndex={0}
                onClick={() => setExpanded((prev) => !prev)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded((prev) => !prev);
                    }
                }}
                data-testid="history-expander-toggle"
                data-expanded={expanded ? "true" : "false"}
            >
                <span className={css.historyExpanderArrow}>{"▶"}</span>
                <span>
                    {`${attempts.length} frühere Zahlungsversuche `}
                    {expanded ? "ausblenden" : "anzeigen"}
                </span>
                <span className={css.historyExpanderCount}>{attempts.length}</span>
            </div>
            {expanded && (
                <div className={css.historyContent} data-testid="history-expander-content">
                    {attempts.map((attempt) => (
                        <CompactRow
                            key={attempt.id}
                            attempt={attempt}
                            onClick={
                                onAttemptClick ? () => onAttemptClick(attempt.id) : undefined
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default HistoryExpander;
